import { z } from "zod";

const tokenResponseSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number().positive(),
  id_token: z.string().min(1),
  refresh_token: z.string().min(1).optional(),
  token_type: z.literal("Bearer"),
});

export const storedSessionSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.number().positive(),
  idToken: z.string().min(1),
  rememberedUntil: z.number().positive(),
  refreshToken: z.string().min(1).optional(),
});

export type StoredSession = z.infer<typeof storedSessionSchema>;

export interface OAuthConfiguration {
  readonly clientId: string;
  readonly domain: string;
  readonly logoutUri: string;
  readonly redirectUri: string;
}

export interface AuthorizationAttempt {
  readonly codeVerifier: string;
  readonly returnTo: string;
  readonly state: string;
}

const attemptSchema = z.object({
  codeVerifier: z.string().min(43),
  returnTo: z.string().startsWith("/"),
  state: z.string().min(16),
});

export const AUTH_ATTEMPT_KEY = "trading-app.oauth-attempt";
export const AUTH_SESSION_KEY = "trading-app.session";
export const REMEMBERED_SESSION_MS = 24 * 60 * 60 * 1_000;

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

async function createAuthorizationAttempt(
  returnTo: string,
): Promise<{ readonly attempt: AuthorizationAttempt; readonly challenge: string }> {
  const verifierBytes = crypto.getRandomValues(new Uint8Array(64));
  const stateBytes = crypto.getRandomValues(new Uint8Array(24));
  const codeVerifier = base64Url(verifierBytes);
  const challenge = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  return {
    attempt: {
      codeVerifier,
      returnTo: returnTo.startsWith("/") ? returnTo : "/positions",
      state: base64Url(stateBytes),
    },
    challenge: base64Url(new Uint8Array(challenge)),
  };
}

export async function authorizationUrl(
  configuration: OAuthConfiguration,
  returnTo: string,
): Promise<{ readonly attempt: AuthorizationAttempt; readonly url: string }> {
  const { attempt, challenge } = await createAuthorizationAttempt(returnTo);
  const url = new URL("/oauth2/authorize", configuration.domain);
  url.search = new URLSearchParams({
    client_id: configuration.clientId,
    code_challenge: challenge,
    code_challenge_method: "S256",
    redirect_uri: configuration.redirectUri,
    response_type: "code",
    scope: "openid email profile",
    state: attempt.state,
  }).toString();
  return { attempt, url: url.toString() };
}

async function tokenRequest(
  configuration: OAuthConfiguration,
  parameters: URLSearchParams,
  fetchImplementation: typeof fetch,
  rememberedUntil: number,
): Promise<StoredSession> {
  parameters.set("client_id", configuration.clientId);
  const response = await fetchImplementation(new URL("/oauth2/token", configuration.domain), {
    body: parameters,
    credentials: "omit",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    method: "POST",
  });
  if (!response.ok) throw new Error("Cognito could not complete the secure session exchange.");
  const tokens = tokenResponseSchema.parse((await response.json()) as unknown);
  return {
    accessToken: tokens.access_token,
    expiresAt: Date.now() + tokens.expires_in * 1_000,
    idToken: tokens.id_token,
    rememberedUntil,
    ...(tokens.refresh_token === undefined ? {} : { refreshToken: tokens.refresh_token }),
  };
}

export async function exchangeAuthorizationCode(
  configuration: OAuthConfiguration,
  callbackUrl: string,
  storage: Storage,
  fetchImplementation: typeof fetch,
): Promise<{ readonly returnTo: string; readonly session: StoredSession }> {
  const attempt = attemptSchema.parse(JSON.parse(storage.getItem(AUTH_ATTEMPT_KEY) ?? "null"));
  const callback = new URL(callbackUrl);
  const code = callback.searchParams.get("code");
  const state = callback.searchParams.get("state");
  if (code === null || state === null || state !== attempt.state) {
    throw new Error("The sign-in response could not be verified.");
  }
  const session = await tokenRequest(
    configuration,
    new URLSearchParams({
      code,
      code_verifier: attempt.codeVerifier,
      grant_type: "authorization_code",
      redirect_uri: configuration.redirectUri,
    }),
    fetchImplementation,
    Date.now() + REMEMBERED_SESSION_MS,
  );
  storage.removeItem(AUTH_ATTEMPT_KEY);
  return { returnTo: attempt.returnTo, session };
}

export async function refreshSession(
  configuration: OAuthConfiguration,
  session: StoredSession,
  fetchImplementation: typeof fetch,
): Promise<StoredSession> {
  if (session.rememberedUntil <= Date.now())
    throw new Error("The remembered Cognito session expired.");
  if (session.refreshToken === undefined)
    throw new Error("The Cognito session cannot be refreshed.");
  const refreshed = await tokenRequest(
    configuration,
    new URLSearchParams({ grant_type: "refresh_token", refresh_token: session.refreshToken }),
    fetchImplementation,
    session.rememberedUntil,
  );
  return { ...refreshed, refreshToken: refreshed.refreshToken ?? session.refreshToken };
}

export function readStoredSession(storage: Storage): StoredSession | null {
  const value = storage.getItem(AUTH_SESSION_KEY);
  if (value === null) return null;
  const parsed = storedSessionSchema.safeParse(JSON.parse(value));
  return parsed.success ? parsed.data : null;
}

export function writeStoredSession(storage: Storage, session: StoredSession): void {
  storage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
}

export function clearStoredSession(storage: Storage): void {
  storage.removeItem(AUTH_ATTEMPT_KEY);
  storage.removeItem(AUTH_SESSION_KEY);
}

export function logoutUrl(configuration: OAuthConfiguration): string {
  const url = new URL("/logout", configuration.domain);
  url.search = new URLSearchParams({
    client_id: configuration.clientId,
    logout_uri: configuration.logoutUri,
  }).toString();
  return url.toString();
}
